import { useQueryClient } from "@tanstack/react-query";
import { useSetAtom } from "jotai";
import { FC, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Pencil } from "@/assets/icons/ds";
import { Button } from "@/components/Interface/Button/Button";
import { FormField } from "@/components/Interface/FormFields/FormField/FormField";
import { Modal } from "@/components/Interface/Modal/Modal";
import {
  getRecipientLines,
  joinParts,
} from "@/components/Portal/Documents/BillingAccounts/billingAccountUtils";
import { useAlert } from "@/hooks/useAlert";
import {
  getGetAccountListQueryKey,
  useCreateBillingAccount,
  useGetCustomer,
  useUpdateBillingAccount,
} from "@/services/api";
import { DrAccount } from "@/services/model";
import { AlertTypes, showsBillingActionBlockedModalAtom } from "@/utils/atoms";
import { isBillingActionBlockedError } from "@/utils/billingActionBlockedError";
import { BACKEND_EMAIL_REGEX } from "@/utils/constants";
import { isBusinessCustomer } from "@/utils/customerUtils";
import { log } from "@/utils/log";

import { BillingAccountAddressFields } from "./BillingAccountAddressFields";
import {
  BillingAccountFormValues,
  getAccountFormValues,
  getCustomerFormValues,
  toBillingAccountRequest,
} from "./billingAccountForm";

const FORM_ID = "billing-account-form";

interface Props {
  /** The Vertragskonto to edit; without it a new one is created. */
  account?: DrAccount;
  onClose: () => void;
  /** Receives the id of a newly created Vertragskonto. */
  onCreated?: (accountId: number) => void;
  /** Compax blocks the change; without it the global modal explains why. */
  onBlocked?: () => void;
}

export const BillingAccountModal: FC<Props> = ({
  account,
  onClose,
  onCreated,
  onBlocked,
}) => {
  const { t } = useTranslation("portal", {
    keyPrefix: "user-account.billingAccounts.modal",
  });
  const { t: tGlobal } = useTranslation();
  const isEdit = !!account;
  const { data: customer } = useGetCustomer();
  // Creating starts from the customer's master data as a read-only summary;
  // "Bearbeiten" (and editing an existing Vertragskonto) shows every field.
  const [isEditingAddress, setIsEditingAddress] = useState(isEdit);
  const formMethods = useForm<BillingAccountFormValues>({
    defaultValues: account
      ? getAccountFormValues(account)
      : getCustomerFormValues(customer),
  });
  const {
    register,
    handleSubmit,
    reset,
    setError,
    watch,
    formState: { errors, isDirty },
  } = formMethods;
  const queryClient = useQueryClient();
  const showAlert = useAlert();
  const showBlockedModal = useSetAtom(showsBillingActionBlockedModalAtom);

  // The customer may still be loading on open; prefill once it arrives.
  const prefilledCustomer = useRef(customer);

  useEffect(() => {
    if (isEdit || !customer || isDirty) {
      return;
    }

    if (prefilledCustomer.current === customer) {
      return;
    }

    prefilledCustomer.current = customer;
    reset(getCustomerFormValues(customer));
  }, [customer, isDirty, isEdit, reset]);

  const handleSuccess = (text: string) => {
    showAlert({ type: AlertTypes.success, text });
    queryClient.invalidateQueries(getGetAccountListQueryKey());
    onClose();
  };

  const handleError = (error: unknown) => {
    if (isBillingActionBlockedError(error)) {
      onClose();

      if (onBlocked) {
        onBlocked();
      } else {
        showBlockedModal(true);
      }

      return;
    }

    const responseData = (error as { response?: { data?: unknown } })?.response
      ?.data;

    if (`${responseData}`.includes("Invalid Email Address")) {
      setError("email", { type: "backendError" });
    }

    showAlert({ type: AlertTypes.error, text: t("error") });
    log("Error saving billing account: " + error);
  };

  const { mutate: createBillingAccount, isLoading: isCreating } =
    useCreateBillingAccount({
      mutation: {
        onSuccess: (response) => {
          if (response.id != null) {
            onCreated?.(response.id);
          }

          handleSuccess(t("createSuccess"));
        },
        onError: handleError,
      },
    });
  const { mutate: updateBillingAccount, isLoading: isUpdating } =
    useUpdateBillingAccount({
      mutation: {
        onSuccess: () => handleSuccess(t("updateSuccess")),
        onError: handleError,
      },
    });
  const isSaving = isCreating || isUpdating;

  const onSubmit = (values: BillingAccountFormValues) => {
    const data = toBillingAccountRequest(values);

    if (account?.id != null) {
      updateBillingAccount({ data, params: { accountId: account.id } });

      return;
    }

    createBillingAccount({ data });
  };

  const values = watch();
  const countryName = tGlobal(`translation:countries.${values.country.ALPHA3}`);

  const addressSummary = (
    <div
      data-testid="billing-account-summary"
      className="flex flex-col gap-2.5"
    >
      <div className="flex items-center gap-2">
        <p className="min-w-0 flex-1 truncate text-body-lg font-semibold leading-md">
          {values.alias}
        </p>
        <Button
          dataTestid="billing-account-edit-address"
          variant="primary"
          size="sm"
          inverted
          icon={<Pencil />}
          onClick={() => setIsEditingAddress(true)}
        >
          {t("editAddress")}
        </Button>
      </div>
      <div className="flex gap-2.5 text-sm leading-md">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          {getRecipientLines({
            companyName: values.companyName,
            firstName: values.firstName,
            lastName: values.lastName,
            postalDeliveryNotes: values.additionalAddress,
          }).map((line, index) => (
            <p key={index}>{line}</p>
          ))}
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <p>{joinParts(values.street, values.houseNumber)}</p>
          <p>{joinParts(values.zip, values.city)}</p>
          <p>{countryName}</p>
        </div>
      </div>
    </div>
  );

  return (
    <Modal
      className="w-full"
      title={
        <div className="flex flex-col gap-2">
          <h2 className="text-headline-sm font-semibold leading-md">
            {t(isEdit ? "editTitle" : "createTitle")}
          </h2>
          <p className="text-sm leading-md">{t("description")}</p>
        </div>
      }
      variant="dark"
      hasScroll={false}
      onClose={onClose}
    >
      <FormProvider {...formMethods}>
        <form
          id={FORM_ID}
          data-testid={FORM_ID}
          className="flex flex-col gap-8 pt-2 text-foreground-primary-inverse"
          noValidate
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="flex flex-col gap-6">
            {isEditingAddress ? (
              <BillingAccountAddressFields
                isCompanyNameRequired={
                  !!customer && isBusinessCustomer(customer)
                }
                disabled={isSaving}
              />
            ) : (
              addressSummary
            )}
            <div className="flex flex-col gap-3">
              <p className="text-base font-semibold leading-none">
                {t("fields.emailHeadline")}
              </p>
              <FormField<BillingAccountFormValues>
                id="billing-account-email"
                name="email"
                type="email"
                label={t("fields.email")}
                register={register}
                errors={errors}
                variant="dark"
                options={{
                  required: tGlobal("translation:label.validation.required"),
                  maxLength: 250,
                  pattern: {
                    value: BACKEND_EMAIL_REGEX,
                    message: tGlobal(
                      "translation:Common.generalForm.validation.emailNotValid",
                    ),
                  },
                  backendError: tGlobal("translation:label.validation.email"),
                  disabled: isSaving,
                }}
              />
            </div>
            <div className="flex flex-col gap-3">
              <p className="text-base font-semibold leading-none">
                {t("fields.referenceHeadline")}
              </p>
              <FormField<BillingAccountFormValues>
                id="billing-account-reference"
                name="reference"
                label={t("fields.reference")}
                register={register}
                errors={errors}
                variant="dark"
                options={{
                  required: tGlobal("translation:label.validation.required"),
                  maxLength: 250,
                  disabled: isSaving,
                }}
              />
              <FormField<BillingAccountFormValues>
                id="billing-account-note"
                name="note"
                label={t("fields.note")}
                register={register}
                errors={errors}
                variant="dark"
                options={{
                  required: false,
                  maxLength: 250,
                  disabled: isSaving,
                }}
              />
            </div>
          </div>
          <div className="flex gap-6">
            <Button
              dataTestid="billing-account-cancel"
              variant="secondary"
              inverted
              fullWidth
              onClick={onClose}
            >
              {t("cancel")}
            </Button>
            <Button
              dataTestid="billing-account-submit"
              type="submit"
              form={FORM_ID}
              variant="primary"
              inverted
              fullWidth
              loading={isSaving}
            >
              {t(isEdit ? "save" : "create")}
            </Button>
          </div>
        </form>
      </FormProvider>
    </Modal>
  );
};
